import React, { useState } from 'react';
import { BarChart3, TrendingUp, AlertCircle } from 'lucide-react';
import { 
  ComposedChart,
  Line, 
  Area,
  Bar,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer
} from 'recharts';
import { cn } from '@/lib/utils';
import { AnalyticsChartData } from '../types/analytics';

interface AnalyticsChartProps {
  data: AnalyticsChartData;
  mode: 'power-temp' | 'energy';
  timeAggregation?: string;
  isLoading?: boolean;
  title?: string;
  height?: number;
  error?: any;
  yAxisRange?: {
    primary?: { min: number; max: number };
    secondary?: { min: number; max: number };
  };
}

export function AnalyticsChart({ 
  data, 
  mode, 
  timeAggregation,
  isLoading = false, 
  title,
  height,
  error,
  yAxisRange
}: AnalyticsChartProps) {
  // State for toggling series visibility
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());
  
  const toggleSeries = (seriesName: string) => {
    const newHiddenSeries = new Set(hiddenSeries);
    if (newHiddenSeries.has(seriesName)) {
      newHiddenSeries.delete(seriesName);
    } else {
      newHiddenSeries.add(seriesName);
    }
    setHiddenSeries(newHiddenSeries);
  };

  // Generate power Y-axis ticks in steps of 500
  const generatePowerTicks = (dataMin: number, dataMax: number) => {
    const min = Math.floor(dataMin / 500) * 500;
    const max = Math.ceil(dataMax / 500) * 500;
    const ticks = [];
    for (let i = min; i <= max; i += 500) {
      ticks.push(i);
    }
    return ticks;
  };

  // Transform data for Recharts
  const chartData = React.useMemo(() => {
    if (!data?.timestamps || data.timestamps.length === 0) return [];
    
    return data.timestamps.map((timestamp, index) => {
      const date = new Date(timestamp);
      let displayTimestamp: string;
      
      // For daily aggregation in energy mode, show just the date
      if (mode === 'energy' && timeAggregation === 'day') {
        displayTimestamp = date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
      } else {
        // Default format with time
        displayTimestamp = date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      const point: any = {
        timestamp: displayTimestamp,
        fullTimestamp: timestamp
      };
      
      data.series.forEach(series => {
        if (series.data[index] !== null && series.data[index] !== undefined) {
          point[series.name] = series.data[index];
        }
      });
      
      return point;
    });
  }, [data, mode, timeAggregation]);
  
  // Separate series by y-axis and filter out hidden ones
  const primarySeries = data?.series?.filter(s => s.yAxisIndex === 0 && !hiddenSeries.has(s.name)) || [];
  const secondarySeries = data?.series?.filter(s => s.yAxisIndex === 1 && !hiddenSeries.has(s.name)) || [];
  const allSeries = data?.series || [];
  
  // Calculate data range for power Y-axis after series are defined
  const powerDataRange = React.useMemo(() => {
    if (!data?.series || data.series.length === 0) return { min: 0, max: 2000 };
    
    let min = Infinity;
    let max = -Infinity;
    
    data.series.forEach(series => {
      if (series.yAxisIndex === 0) { // Primary axis (power)
        series.data.forEach(value => {
          if (typeof value === 'number' && isFinite(value)) {
            min = Math.min(min, value);
            max = Math.max(max, value);
          }
        });
      }
    });
    
    return {
      min: min === Infinity ? 0 : Math.max(0, min),
      max: max === -Infinity ? 2000 : max
    };
  }, [data]);
  
  const powerTicks = generatePowerTicks(powerDataRange.min, powerDataRange.max);
  
  // Autopilot-style color theme
  const THEME = {
    consumption: {
      line: '#0E7EE4',     // Blue 50
      area: 'rgba(14, 126, 228, 0.1)'
    },
    solar: {
      actual: '#F2AA32',    // Orange 40
      expected: '#F26428'   // Orange
    },
    temperature: {
      outdoor: '#109095',   // Teal
      setTemp: '#0E7EE4',   // Blue 50
      indoor: '#14B0BC'     // Mid Green
    },
    working: '#0A4E8B',     // Dark Blue
    ahu: {
      cooling: '#0E7EE4',   // Blue 50
      fan: '#8BC6FF'        // Light Blue
    }
  };
  
  // Enhanced color mapping function to exactly match Autopilot tab
  const getSeriesColor = (seriesName: string) => {
    const name = seriesName.toLowerCase();
    
    // Exact Autopilot colors - Power & Temperature mode
    if (name.includes('total_power') || name.includes('total power')) return '#000000'; // Black for Total Power
    // Check Non A/C Power BEFORE A/C Power to avoid matching conflicts
    if (name.includes('non_a/c_power') || name.includes('non a/c power') || name.includes('non ac power') || name.includes('non_ac_power')) return '#9CA3AF'; // Gray for Non A/C Power
    if (name.includes('a/c_power') || name.includes('a/c power')) return THEME.consumption.line; // Blue for A/C Power
    if (name.includes('grid_import') || name.includes('grid import')) return '#7D3BBF'; // Purple for Grid Import
    if (name.includes('solar_power') || name.includes('solar power') || (name.includes('solar') && name.includes('power'))) {
      if (name.includes('expected')) return THEME.solar.expected;
      return THEME.solar.actual;
    }
    if (name.includes('outdoor_temp') || name.includes('outdoor temp')) return THEME.temperature.outdoor;
    if (name.includes('indoor_temp') || name.includes('indoor temp')) return THEME.temperature.indoor;
    
    // General fallbacks using exact Autopilot colors
    if (name.includes('temperature') || name.includes('temp')) return THEME.temperature.outdoor;
    if (name.includes('solar')) return THEME.solar.actual;
    if (name.includes('power') || name.includes('energy')) return THEME.consumption.line;
    if (name.includes('cooling')) return THEME.ahu.cooling;
    if (name.includes('fan')) return THEME.ahu.fan;
    
    // Fallback colors from Autopilot
    const fallbackColors = [THEME.working, THEME.solar.expected, THEME.temperature.setTemp, THEME.ahu.fan];
    return fallbackColors[Math.abs(seriesName.length) % fallbackColors.length];
  };
  
  // Get area fill color with exact Autopilot opacity
  const getAreaFillColor = (seriesName: string) => {
    const name = seriesName.toLowerCase();
    
    if (name.includes('total_power') || name.includes('total power')) return 'rgba(0, 0, 0, 0.03)'; // Very light black with low opacity for Total Power
    // Check Non A/C Power BEFORE A/C Power to avoid matching conflicts
    if (name.includes('non_a/c_power') || name.includes('non a/c power') || name.includes('non ac power') || name.includes('non_ac_power')) return 'rgba(156, 163, 175, 0.1)'; // Gray with opacity for Non A/C Power
    if (name.includes('a/c_power') || name.includes('a/c power')) return THEME.consumption.area; // Blue for A/C Power
    if (name.includes('grid_import') || name.includes('grid import')) return 'rgba(125, 59, 191, 0.1)'; // Purple with opacity for Grid Import
    if (name.includes('solar_power') || name.includes('solar power')) return 'rgba(242, 170, 50, 0.1)';
    
    // Default area fill
    const baseColor = getSeriesColor(seriesName);
    const hex = baseColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, 0.1)`;
  };
  
  // Determine if series should have area fill (only power/energy series in power-temp mode)
  const shouldHaveArea = (seriesName: string, yAxisIndex: number) => {
    if (mode === 'energy') return false; // No area in energy mode
    if (yAxisIndex !== 0) return false; // Only primary axis (power) gets area
    
    const name = seriesName.toLowerCase();
    // Area fill for power metrics except expected solar
    return (name.includes('total_power') || 
            name.includes('non_a/c_power') ||
            name.includes('non_ac_power') ||
            name.includes('a/c_power') ||
            name.includes('grid_import') || 
            name.includes('solar_power') ||
            name.includes('total power') ||
            name.includes('non a/c power') ||
            name.includes('non ac power') ||
            name.includes('a/c power') ||
            name.includes('grid import') ||
            name.includes('solar power')) &&
           !name.includes('expected');
  };

  // Loading State
  if (isLoading) {
    return (
      <div 
        className="relative overflow-hidden w-full flex items-center justify-center"
        style={height ? { height: `${height}px` } : { height: '100%' }}
      >
        {title && (
          <div className="absolute top-3 left-4 z-10">
            <div className="h-4 w-32 bg-light-grey rounded animate-pulse"></div>
          </div>
        )}
        <div className="text-center">
          <div className="flex items-center justify-center mb-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent"></div>
            </div>
          </div>
          <div className="heading-7 text-dark-blue mb-1">Loading Analytics</div>
          <div className="paragraph-11 text-dark-grey">Fetching your data...</div>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div 
        className="relative overflow-hidden w-full flex items-center justify-center"
        style={height ? { height: `${height}px` } : { height: '100%' }}
      >
        {title && (
          <div className="absolute top-3 left-4 z-10">
            <h3 className="heading-8 text-dark-blue">{title}</h3>
          </div>
        )}
        <div className="text-center max-w-sm">
          <div className="flex items-center justify-center mb-3">
            <div className="w-10 h-10 bg-danger/10 rounded-full flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-danger" />
            </div>
          </div>
          <div className="heading-8 text-dark-blue mb-1">Failed to Load Data</div>
          <div className="paragraph-9 text-dark-grey mb-3">
            {error?.message || 'There was an error loading the analytics data. Please try again.'}
          </div>
          <button 
            onClick={() => window.location.reload()}
            className="px-3 py-1.5 paragraph-11 font-medium bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty State
  if (!data || data.timestamps.length === 0) {
    return (
      <div 
        className="relative overflow-hidden w-full flex items-center justify-center"
        style={height ? { height: `${height}px` } : { height: '100%' }}
      >
        {title && (
          <div className="absolute top-3 left-4 z-10">
            <h3 className="heading-8 text-dark-blue">{title}</h3>
          </div>
        )}
        <div className="text-center max-w-sm">
          <div className="flex items-center justify-center mb-3">
            <div className="w-10 h-10 bg-light-grey rounded-full flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-dark-grey" />
            </div>
          </div>
          <div className="heading-7 text-dark-blue mb-1 text-center">No Data Available</div>
          <div className="paragraph-12 text-dark-grey mb-3">
            Select metrics and date range to view analytics data.
          </div>
          <div className="flex items-center justify-center gap-4 text-dark-grey">
            <div className="flex items-center gap-1.5">
              <TrendingUp className="w-4 h-4" />
              <span className="paragraph-9">Power & Temperature</span>
            </div>
            <span className="paragraph-9">•</span>
            <div className="flex items-center gap-1.5">
              <BarChart3 className="w-4 h-4" />
              <span className="paragraph-9">Energy Analysis</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="relative overflow-hidden w-full"
      style={height ? { height: `${height}px` } : { height: '100%' }}
    >
      {title && (
        <div className="absolute top-3 left-4 z-10">
          <h3 className="heading-8 text-dark-blue">{title}</h3>
        </div>
      )}
      
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={chartData} margin={{ top: title ? 70 : 40, right: 15, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="2 2" stroke="#EEEEEE" />
          
          <XAxis 
            dataKey="timestamp"
            tick={{ fontSize: 10, fill: '#666666', fontFamily: "'Inter', sans-serif" }}
            axisLine={{ stroke: '#EEEEEE' }}
            tickLine={{ stroke: '#EEEEEE' }}
            interval="preserveStartEnd"
            domain={['dataMin', 'dataMax']}
            type="category"
          />
          
          {/* Primary Y-Axis (Power/Energy) */}
          <YAxis 
            yAxisId="primary"
            orientation="left"
            tick={{ fontSize: 10, fill: '#666666', fontFamily: "'Inter', sans-serif" }}
            axisLine={{ stroke: '#EEEEEE' }}
            tickLine={{ stroke: '#EEEEEE' }}
            domain={[powerDataRange.min, powerDataRange.max]}
            ticks={powerTicks}
            type="number"
            tickFormatter={(value) => {
              if (value >= 1000) {
                return `${(value / 1000).toFixed(1)}k`;
              }
              return value.toString();
            }}
            label={{ 
              value: mode === 'energy' ? 'Energy (kWh)' : 'Power (kW)', 
              angle: -90, 
              position: 'insideLeft',
              style: { textAnchor: 'middle', fontSize: '10px', fill: '#666666', fontFamily: "'Inter', sans-serif" }
            }}
          />
          
          {/* Secondary Y-Axis (Temperature) */}
          {secondarySeries.length > 0 && (
            <YAxis 
              yAxisId="secondary"
              orientation="right"
              tick={{ fontSize: 10, fill: '#666666', fontFamily: "'Inter', sans-serif" }}
              axisLine={{ stroke: '#EEEEEE' }}
              tickLine={{ stroke: '#EEEEEE' }}
              domain={[15, 50]}
              ticks={[15, 20, 25, 30, 35, 40, 45, 50]}
              type="number"
              label={{ 
                value: 'Temperature (°C)', 
                angle: 90, 
                position: 'insideRight',
                style: { textAnchor: 'middle', fontSize: '10px', fill: '#666666', fontFamily: "'Inter', sans-serif" }
              }}
            />
          )}
          
          <Tooltip 
            content={({ active, payload, label }) => {
              if (!active || !payload || !payload.length) return null;
              
              // Filter out hidden series from tooltip
              const visiblePayload = payload.filter(entry => !hiddenSeries.has(entry.dataKey as string));
              
              if (visiblePayload.length === 0) return null;
              
              return (
                <div className="bg-white border border-light-grey rounded-md p-3 shadow-lg">
                  <p className="font-medium text-dark-blue mb-2">{label}</p>
                  {visiblePayload.map((entry, index) => {
                    const series = data?.series?.find(s => s.name === entry.dataKey);
                    return (
                      <div key={index} className="flex items-center justify-between gap-3 mb-1">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-sm" 
                            style={{ backgroundColor: entry.color }}
                          />
                          <span className="text-sm">{entry.dataKey}</span>
                        </div>
                        <span className="text-sm font-medium">
                          {entry.value?.toFixed(2)} {series?.unit || ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            }}
          />
          
          {/* Custom Toggleable Legend */}
          <g>
            <foreignObject x={0} y={title ? 35 : 5} width="100%" height={30}>
              <div className="flex flex-wrap items-center justify-center gap-4 px-4">
                {allSeries.map((series) => {
                  const isHidden = hiddenSeries.has(series.name);
                  const isExpected = series.name.toLowerCase().includes('expected');
                  const actualColor = getSeriesColor(series.name); // Use same color function as chart
                  return (
                    <button
                      key={series.name}
                      onClick={() => toggleSeries(series.name)}
                      className={`flex items-center gap-2 px-2 py-1 rounded-md transition-all duration-200 hover:bg-gray-50 ${
                        isHidden ? 'opacity-40' : 'opacity-100'
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        {isExpected ? (
                          // Dashed line indicator for expected series
                          <div 
                            className="w-3 h-0.5 rounded-sm" 
                            style={{ 
                              backgroundColor: isHidden ? '#ccc' : actualColor,
                              backgroundImage: isHidden ? 'none' : 'repeating-linear-gradient(90deg, transparent, transparent 2px, white 2px, white 3px)'
                            }}
                          />
                        ) : (
                          // Solid dot for regular series
                          <div 
                            className="w-2.5 h-2.5 rounded-full" 
                            style={{ backgroundColor: isHidden ? '#ccc' : actualColor }}
                          />
                        )}
                        <span 
                          className={`text-xs font-medium ${
                            isHidden ? 'text-gray-400' : 'text-gray-700'
                          }`}
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          {series.name}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </foreignObject>
          </g>
          
          {/* Render Primary Series (Power/Energy) */}
          {primarySeries.map((series) => {
            const color = getSeriesColor(series.name);
            const hasArea = shouldHaveArea(series.name, series.yAxisIndex);
            const isExpected = series.name.toLowerCase().includes('expected');
            
            if (mode === 'energy') {
              // Energy mode: Special handling for different series types
              if (isExpected) {
                // Expected Solar Power as dashed line in energy mode
                return (
                  <Line
                    key={series.name}
                    yAxisId="primary"
                    type="linear"
                    dataKey={series.name}
                    stroke={color}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    activeDot={{ r: 3, fill: color, stroke: '#fff', strokeWidth: 1 }}
                    connectNulls={false}
                  />
                );
              } else {
                // Check for stacking combinations
                const seriesNames = primarySeries.map(s => s.name.toLowerCase());
                const hasGridAndSolar = seriesNames.includes('grid import') && seriesNames.includes('solar power');
                const hasAcAndNonAc = seriesNames.includes('a/c power') && (seriesNames.includes('non a/c power') || seriesNames.includes('non ac power'));
                
                let stackId = undefined;
                if (hasGridAndSolar && (series.name.toLowerCase().includes('grid import') || series.name.toLowerCase().includes('solar power'))) {
                  stackId = 'grid-solar-stack';
                } else if (hasAcAndNonAc && (series.name.toLowerCase().includes('a/c power') || series.name.toLowerCase().includes('non a/c power') || series.name.toLowerCase().includes('non ac power'))) {
                  stackId = 'ac-nonac-stack';
                }
                
                // Regular bars for energy data with optional stacking
                return (
                  <Bar
                    key={series.name}
                    yAxisId="primary"
                    dataKey={series.name}
                    fill={color}
                    opacity={0.8}
                    radius={[1, 1, 0, 0]}
                    stackId={stackId}
                  />
                );
              }
            } else {
              // Power & Temperature mode: Use lines with area
              return (
                <Area
                  key={series.name}
                  yAxisId="primary"
                  type="linear"
                  dataKey={series.name}
                  stroke={color}
                  strokeWidth={1.5}
                  strokeDasharray={isExpected ? "5 5" : undefined}
                  fill={hasArea ? getAreaFillColor(series.name) : 'transparent'}
                  fillOpacity={1}
                  dot={false}
                  activeDot={{ r: 3, fill: color, stroke: '#fff', strokeWidth: 1 }}
                  connectNulls={false}
                />
              );
            }
          })}
          
          {/* Render Secondary Series (Temperature) */}
          {secondarySeries.map((series) => {
            const color = getSeriesColor(series.name);
            return (
              <Line
                key={series.name}
                yAxisId="secondary"
                type="linear"
                dataKey={series.name}
                stroke={color}
                strokeWidth={1.5}
                dot={false}
                activeDot={{ r: 3, fill: color, stroke: '#fff', strokeWidth: 1 }}
                connectNulls={false}
              />
            );
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

